import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, Package, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "../lib/api";
import { invalidateAppSettings } from "../lib/useAppSettings";

/**
 * TIME-1 question sets — the editor the Safety Director lives in.
 *
 * Awareness, not a quiz: load a hundred statements if you like, each person is asked a couple a
 * day, picked at random for them and stable for that whole day. Nothing here ever refuses a
 * punch — an answer that matters is flagged for the foreman on the real time entry.
 */
const KIND_ICON = { safety: ShieldCheck, material: Package };
const PER_DAY_KEY = { safety: "clockin_questions_per_day", material: "clockout_questions_per_day" };
const MASTER_LABEL = {
  safety: "Safety prompt at clock-in",
  material: "Material questions at clock-out",
};

const newQuestion = () => ({
  id: `new-${Math.random().toString(36).slice(2, 9)}`,
  text: "", type: "ack", flag_answer: "", photo_required: false, active: true,
});

export default function QuestionSetsPanel() {
  const [meta, setMeta] = useState(null);
  const [kind, setKind] = useState("safety");
  const [newCode, setNewCode] = useState("");

  const load = () => api.get("/time-questions/sets").then((r) => setMeta(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sets = useMemo(
    () => (meta?.sets || []).filter((s) => s.kind === kind), [meta, kind],
  );
  if (!meta) return null;
  const canEdit = !!meta.can_edit?.[kind];
  const perDay = meta.per_day?.[kind] ?? 0;
  const masterOn = !!meta.masters?.[kind];

  const saveSet = async (scopeCode, questions) => {
    try {
      await api.put("/time-questions/sets", { kind, scope_code: scopeCode, questions });
      toast.success(`${scopeCode === "*" ? "All trades" : scopeCode} saved`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const removeSet = async (scopeCode) => {
    try {
      await api.delete(`/time-questions/sets/${kind}/${encodeURIComponent(scopeCode)}`);
      toast.success(`${scopeCode} set removed`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };
  const savePerDay = async (value) => {
    try {
      await api.patch("/settings/app", { [PER_DAY_KEY[kind]]: Number(value) });
      invalidateAppSettings();
      toast.success("Questions per day saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const saveRoles = async (roles) => {
    try {
      await api.put("/time-questions/editor-roles", { kind, roles });
      toast.success("Who may edit this set saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="mt-4 rounded-md border border-gray-200 p-3" data-testid="question-sets-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs font-semibold text-gray-700">Question sets</div>
        <div className="flex items-center gap-1 ml-1">
          {(meta.kinds || []).map((k) => {
            const Icon = KIND_ICON[k.value] || ShieldCheck;
            return (
              <button key={k.value} type="button" onClick={() => setKind(k.value)}
                      data-testid={`question-kind-${k.value}`}
                      className={`h-7 px-2.5 rounded-md border text-xs inline-flex items-center gap-1 transition-colors ${
                        kind === k.value ? "bg-gray-900 text-white border-gray-900"
                                         : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}>
                <Icon className="w-3.5 h-3.5" /> {k.label}
              </button>
            );
          })}
        </div>
        {!canEdit && (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-gray-500"
                data-testid="question-sets-readonly">
            <Lock className="w-3 h-3" /> Read only for {meta.my_role || "your role"} — an admin
            decides who edits this set
          </span>
        )}
      </div>

      <p className="mt-1 text-[11px] text-gray-400">
        Load as many statements as you like; each person is asked{" "}
        <b className="text-gray-600">{perDay || "all of them"}</b> a day, picked at random for them
        and the same for the whole of that day. A trade with no set of its own is asked the
        All-trades set. Nothing is asked unless <b>{MASTER_LABEL[kind]}</b> is on above
        {masterOn ? " — it is on." : " — it is off right now."}
      </p>

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <label className="text-[11px] text-gray-500 inline-flex items-center gap-1">
          Asked per person per day
          <Input type="number" min={0} max={20} defaultValue={perDay} disabled={!canEdit}
                 className="h-7 w-16 text-xs" data-testid={`question-per-day-${kind}`}
                 onBlur={(e) => Number(e.target.value) !== perDay && savePerDay(e.target.value)} />
          <span className="text-gray-400">0 = ask every question in the set</span>
        </label>
      </div>

      {meta.can_set_roles && (
        <div className="mt-2 flex items-start gap-2 flex-wrap text-[11px] text-gray-500"
             data-testid={`question-editor-roles-${kind}`}>
          <span className="pt-0.5">Who may edit these (Admin always can):</span>
          {(meta.roles || []).map((r) => {
            const on = (meta.editor_roles?.[kind] || []).includes(r.value);
            return (
              <label key={r.value} className="inline-flex items-center gap-1">
                <input type="checkbox" checked={on}
                       data-testid={`question-editor-role-${kind}-${r.value}`}
                       onChange={() => saveRoles(on
                         ? (meta.editor_roles[kind] || []).filter((x) => x !== r.value)
                         : [...(meta.editor_roles[kind] || []), r.value])} />
                {r.label}
              </label>
            );
          })}
        </div>
      )}

      <div className="mt-3 space-y-2">
        {sets.length === 0 && (
          <p className="text-[11px] text-gray-400" data-testid="question-sets-empty">
            No set for this yet — nothing will be asked.
          </p>
        )}
        {sets.map((s) => (
          <SetCard key={`${s.kind}-${s.scope_code}`} set={s} types={meta.types || []}
                   canEdit={canEdit} onSave={(qs) => saveSet(s.scope_code, qs)}
                   onRemove={s.scope_code === "*" ? null : () => removeSet(s.scope_code)} />
        ))}
      </div>

      {canEdit && (
        <div className="mt-2 flex items-center gap-2">
          <Input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                 placeholder="Trade / scope code (e.g. TRA)" className="h-8 w-56 text-xs"
                 data-testid="question-set-new-code" />
          <Button size="sm" variant="outline" className="h-8 text-xs"
                  disabled={!newCode.trim() || sets.some((s) => s.scope_code === newCode.trim())}
                  data-testid="question-set-add"
                  onClick={() => { saveSet(newCode.trim(), [newQuestion()]); setNewCode(""); }}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add a trade set
          </Button>
        </div>
      )}
    </div>
  );
}

function SetCard({ set, types, canEdit, onSave, onRemove }) {
  const [rows, setRows] = useState(set.questions || []);
  useEffect(() => setRows(set.questions || []), [set]);
  const dirty = JSON.stringify(rows) !== JSON.stringify(set.questions || []);
  const code = set.scope_code;
  const patch = (i, p) => setRows(rows.map((q, j) => (j === i ? { ...q, ...p } : q)));

  return (
    <div className="rounded-md border border-gray-200" data-testid={`question-set-${set.kind}-${code}`}>
      <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
        <b className="text-xs text-gray-900">{code === "*" ? "All trades" : code}</b>
        <span className="text-[11px] text-gray-500">
          {rows.filter((q) => q.active !== false && (q.text || "").trim()).length} question(s) · v{set.version}
          {set.updated_by ? ` · last changed by ${set.updated_by}` : ""}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {canEdit && dirty && (
            <Button size="sm" className="h-7 text-xs" data-testid={`question-set-save-${code}`}
                    onClick={() => onSave(rows.filter((q) => (q.text || "").trim()))}>
              Save
            </Button>
          )}
          {canEdit && onRemove && (
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400"
                    title="Remove this trade's set" data-testid={`question-set-remove-${code}`}
                    onClick={onRemove}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      <div className="p-2 space-y-1.5">
        {rows.map((q, i) => (
          <div key={q.id} className="flex items-center gap-1.5 flex-wrap"
               data-testid={`question-row-${code}-${i}`}>
            <Input value={q.text} disabled={!canEdit}
                   onChange={(e) => patch(i, { text: e.target.value })}
                   placeholder="A statement to acknowledge, or a question"
                   className="h-7 text-xs flex-1 min-w-[240px]"
                   data-testid={`question-text-${code}-${i}`} />
            <select value={q.type} disabled={!canEdit}
                    onChange={(e) => patch(i, { type: e.target.value, flag_answer: "" })}
                    className="h-7 border border-gray-300 rounded px-1 text-xs bg-white"
                    data-testid={`question-type-${code}-${i}`}>
              {types.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {q.type === "yes_no" && (
              <select value={q.flag_answer || ""} disabled={!canEdit}
                      onChange={(e) => patch(i, { flag_answer: e.target.value })}
                      className="h-7 border border-gray-300 rounded px-1 text-xs bg-white"
                      title="Which answer should tell the foreman"
                      data-testid={`question-flag-${code}-${i}`}>
                <option value="">never flag</option>
                <option value="yes">flag on Yes</option>
                <option value="no">flag on No</option>
              </select>
            )}
            <label className="text-[11px] text-gray-500 inline-flex items-center gap-1">
              <input type="checkbox" checked={!!q.photo_required} disabled={!canEdit}
                     data-testid={`question-photo-${code}-${i}`}
                     onChange={(e) => patch(i, { photo_required: e.target.checked })} />
              photo
            </label>
            <label className="text-[11px] text-gray-500 inline-flex items-center gap-1">
              <input type="checkbox" checked={q.active !== false} disabled={!canEdit}
                     data-testid={`question-active-${code}-${i}`}
                     onChange={(e) => patch(i, { active: e.target.checked })} />
              in rotation
            </label>
            {canEdit && (
              <button type="button" className="text-gray-400 hover:text-red-600"
                      data-testid={`question-remove-${code}-${i}`}
                      onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <button type="button" className="text-xs text-blue-700 underline"
                  data-testid={`question-add-${code}`}
                  onClick={() => setRows([...rows, newQuestion()])}>
            + Add a statement or question
          </button>
        )}
        <p className="text-[10px] text-gray-400">
          A statement or a Yes/No must be answered before the punch is recorded; free text is
          always optional. A flagged answer never blocks the punch — it lands on the timesheet for
          the foreman.
        </p>
      </div>
    </div>
  );
}
